import React from "react";
import {
    FileText,
    Store,
    Clock,
    CircleCheck,
    LayoutDashboard,
    Inbox,
    Eye,
    Mail,
} from "lucide-react";
import { IMAGES } from "../../utils/images";

const steps = [
    {
        number: "1",
        icon: FileText,
        title: "Choose your type",
        subtitle: "Shop, supplier, or both",
    },
    {
        number: "2",
        icon: Store,
        title: "Add your details",
        subtitle: "Name, address, photos",
    },
    {
        number: "3",
        icon: Clock,
        title: "Set services & hours",
        subtitle: "What you offer & when",
    },
    {
        number: "4",
        icon: CircleCheck,
        title: "Go live",
        subtitle: "Start receiving enquiries",
    },
];

const dashboardFeatures = [
    {
        icon: Inbox,
        text: "Incoming orders with file previews",
    },
    {
        icon: Eye,
        text: "Job detail view with customer info",
    },
    {
        icon: Clock,
        text: "Active, completed, and past jobs tracking",
    },
    {
        icon: Mail,
        text: "Quick customer communication",
    },
];

const AboutGrowthSection = () => {
    return (
        <section className="bg-white border-y border-[#e4e5e7]">
            <div className="max-w-[1200px] mx-auto px-[16px] md:px-[40px] py-[56px] md:py-[80px]">

                {/* Header */}
                <div className="text-center mb-[48px]">
                    <p className="font-semibold text-[13px] text-[#ff5323] tracking-[0.5px] uppercase mb-[8px]">
                        For print shops & suppliers
                    </p>

                    <h2 className="font-semibold text-[28px] md:text-[36px] text-[#212326] tracking-[-0.5px] mb-[12px]">
                        Your digital shopfront. Your growth engine.
                    </h2>

                    <p className="font-medium text-[16px] text-[#717182] leading-[1.6] max-w-[620px] mx-auto">
                        Whether you're a print shop receiving files or a supplier selling
                        materials - get listed in under 5 minutes and start receiving orders
                        and enquiries immediately.
                    </p>
                </div>

                {/* Steps */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-[12px] md:gap-[16px] mb-[48px]">
                    {steps.map((step) => {
                        const Icon = step.icon;

                        return (
                            <div
                                key={step.number}
                                className="relative bg-[#f8f8fa] rounded-[14px] p-[20px] border border-[#e4e5e7] text-center"
                            >
                                <div className="w-[24px] h-[24px] rounded-full bg-[#ff5323] text-white font-semibold text-[12px] flex items-center justify-center mx-auto mb-[12px]">
                                    {step.number}
                                </div>

                                <div className="w-[40px] h-[40px] rounded-[10px] bg-white flex items-center justify-center mx-auto mb-[10px] text-[#ff5323]">
                                    <Icon className="w-[20px] h-[20px]" />
                                </div>

                                <p className="font-semibold text-[14px] text-[#212326] mb-[2px]">
                                    {step.title}
                                </p>

                                <p className="font-medium text-[12px] text-[#9298a0]">
                                    {step.subtitle}
                                </p>
                            </div>
                        );
                    })}
                </div>

                {/* Dashboard Section */}
                <div className="bg-[#212326] rounded-[20px] overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-2">

                        {/* Left */}
                        <div className="p-[32px] md:p-[48px] flex flex-col justify-center order-2 lg:order-1">

                            <div className="inline-flex items-center gap-[6px] px-[12px] py-[5px] rounded-full bg-white/10 mb-[20px] w-fit">
                                <LayoutDashboard className="w-[13px] h-[13px] text-[#ff8a65]" />
                                <span className="font-semibold text-[11px] text-white/70 uppercase tracking-[0.5px]">
                                    Store Dashboard
                                </span>
                            </div>

                            <h3 className="font-semibold text-[22px] md:text-[26px] text-white tracking-[-0.3px] mb-[12px]">
                                Everything in one place
                            </h3>

                            <p className="font-medium text-[13px] text-white/40 mb-[4px]">
                                Print shops get a full dashboard. Suppliers get a streamlined
                                portal with enquiry tracking and settings.
                            </p>

                            <p className="font-medium text-[15px] text-white/55 leading-[1.7] mb-[28px]">
                                Your centralised command centre for managing orders, viewing
                                customer files, tracking job progress, and accessing business
                                analytics - all from one clean dashboard.
                            </p>

                            <div className="flex flex-col gap-[14px]">
                                {dashboardFeatures.map((item, i) => {
                                    const Icon = item.icon;

                                    return (
                                        <div key={i} className="flex items-center gap-[12px]">
                                            <div className="w-[32px] h-[32px] rounded-[8px] bg-white/10 flex items-center justify-center text-[#ff8a65] shrink-0">
                                                <Icon className="w-[16px] h-[16px]" />
                                            </div>

                                            <p className="font-medium text-[14px] text-white/80">
                                                {item.text}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Right Image */}
                        <div className="relative min-h-[280px] lg:min-h-[400px] order-1 lg:order-2">
                            <img
                                src={IMAGES.growth}
                                alt="Dashboard analytics"
                                className="absolute inset-0 w-full h-full object-cover opacity-80"
                            />
                            {/* <div className="absolute inset-0 bg-gradient-to-r from-[#212326] via-transparent to-transparent" /> */}
                        </div>

                    </div>
                </div>

            </div>
        </section>
    );
};

export default AboutGrowthSection;
